import {
  Chip,
  FormControl,
  FormGroup,
  FormLabel,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useApiClient from '~/src/hooks/useApiClient';
import { FilterOptions, FiltersState } from '../store';

const useStyles = makeStyles(({ breakpoints, palette, spacing }) => ({
  wrapper: {},
  paper: {
    padding: spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
    width: spacing(100),
    [breakpoints.down('sm')]: {
      width: '100%',
      minWidth: spacing(50),
      maxWidth: spacing(90),
    },
  },
  section: {
    marginBottom: spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing(2),
    marginTop: spacing(2),
  },
  connectorTypes: {
    fontWeight: 'bold',
    fontSize: '1rem',
    color: `${palette.text.primary} !important`,
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing(1),
  },
  tagAutocomplete: {
    '& .MuiAutocomplete-tag': {
      margin: '2px',
    },
  },
}));

const CONNECTOR_OPTIONS = [
  'sql-server',
  'snowflake',
  'csv',
  'googlesheet',
  'excel',
  'salesforce',
  'totango',
  'bigquery',
  'hubspot',
];
const CONNECTOR_LABELS: Record<string, string> = {
  'sql-server': 'SQL Server',
  snowflake: 'Snowflake',
  csv: 'CSV',
  googlesheet: 'Google Sheet',
  excel: 'Excel',
  salesforce: 'Salesforce',
  totango: 'Totango',
  bigquery: 'BigQuery',
  hubspot: 'HubSpot',
};

const defaultFilters: FiltersState = {
  projectState: FilterOptions.All,
  connectorTypes: [],
  tagNames: [],
};

const Filters = ({ className, open, onClose, onChange, selected }) => {
  const { t } = useTranslation('projects');
  const classes = useStyles();
  const apiClient = useApiClient();
  const [localFilters, setLocalFilters] = useState<FiltersState>(
    selected && Object.keys(selected).length > 0 ? selected : defaultFilters,
  );
  const [allTags, setAllTags] = useState<string[]>([]);

  const loadTags = async () => {
    try {
      const response = await apiClient.get<string[]>('/projects/tags');
      setAllTags(response.data || []);
    } catch (err) {
      // ignore error
    }
  };

  useEffect(() => {
    if (open) {
      loadTags();
    }
  }, [open]);

  if (!open) return null;

  const handleChange = (key: keyof FiltersState, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onChange(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(defaultFilters);
    onChange(defaultFilters);
  };

  const toggleConnectorType = (type: string) => {
    const normalizedType = type.toLowerCase();
    setLocalFilters((prev) => {
      const current = prev.connectorTypes || [];
      const newList = current.includes(normalizedType)
        ? current.filter((connector) => connector !== normalizedType)
        : [...current, normalizedType];
      return { ...prev, connectorTypes: newList };
    });
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box className={cx(classes.wrapper, className)}>
        <Paper elevation={open ? 3 : 0} className={cx(classes.paper)}>
          <FormControl component="fieldset" className={classes.section}>
            <RadioGroup
              aria-label="project-state"
              name="projectState"
              value={localFilters.projectState || ''}
              onChange={(e) => handleChange('projectState', e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={t('filterProjects.options.all')}
              />
              <FormControlLabel
                value="complete"
                control={<Radio />}
                label={t('filterProjects.options.complete')}
              />
              <FormControlLabel
                value="incomplete"
                control={<Radio />}
                label={t('filterProjects.options.incomplete')}
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" className={classes.section}>
            <FormLabel component="legend" className={classes.connectorTypes}>
              {t('filterProjects.connectorLabel')}
            </FormLabel>
            <FormGroup row className={classes.container}>
              {CONNECTOR_OPTIONS.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={localFilters.connectorTypes?.includes(type)}
                      onChange={() => toggleConnectorType(type)}
                      color="primary"
                    />
                  }
                  label={CONNECTOR_LABELS[type] || type}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset" className={classes.section}>
            <FormLabel component="legend" className={classes.connectorTypes}>
              {t('filterProjects.projectDateLabel')}
            </FormLabel>
            <FormGroup row className={classes.container}>
              <TextField
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={localFilters.fromDate || ''}
                style={{ flex: 1 }}
                onChange={(e) => handleChange('fromDate', e.target.value)}
              />
              <TextField
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={localFilters.toDate || ''}
                style={{ flex: 1 }}
                onChange={(e) => handleChange('toDate', e.target.value)}
              />
            </FormGroup>
          </FormControl>
          <Box className={classes.section}>
            <FormLabel
              className={classes.connectorTypes}
            >
              {t('filterProjects.tagsLabel')}
            </FormLabel>
            <Autocomplete
              className={classes.tagAutocomplete}
              multiple
              freeSolo
              options={allTags}
              value={localFilters.tagNames || []}
              onChange={(_, newValue) => handleChange('tagNames', newValue)}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                  <Chip
                    label={option}
                    color="primary"
                    variant="filled"
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t('filterProjects.tagSearchPlaceholder')}
                  size="small"
                />
              )}
            />
          </Box>

          <Box className={classes.buttonGroup}>
            <Button variant="outlined" onClick={handleReset}>
              {t('filterProjects.buttonLabel.reset')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleApply}>
              {t('filterProjects.buttonLabel.apply')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </ClickAwayListener>
  );
};

Filters.propTypes = {
  className: PropTypes.string,
  selected: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

Filters.defaultProps = {
  className: '',
  selected: {},
  open: false,
};

export default Filters;
